'use client'

import { ContentFile } from "@/lib/types"
import { cn } from "@/lib/utils"
import { useRef, useState } from "react"
import { motion, AnimatePresence } from "motion/react"
import { FileCard } from "@/components/filesV2/file-card"
import { Button } from "@/components/ui/button"
import { Trash2 } from "lucide-react"
import React from "react"

export interface FileDisplayProps {
    files: ContentFile[]
    onFileSelect?: (file: ContentFile) => void
    onFileDelete?: (file: ContentFile) => void
    allowDelete?: boolean
    cardSize?: 'small' | 'large'
    className?: string
    selectedFileId?: string
    bgGradientColor?: string
}

export function FileDisplay({
    files,
    onFileSelect,
    onFileDelete,
    allowDelete = true,
    cardSize = 'large',
    className,
    selectedFileId,
    bgGradientColor
}: FileDisplayProps) {
    const scrollContainerRef = useRef<HTMLDivElement>(null)
    const [isDragging, setIsDragging] = useState(false)
    const [startX, setStartX] = useState(0)
    const [scrollLeft, setScrollLeft] = useState(0)
    const [isHovered, setIsHovered] = useState(false)

    // Add scroll position state
    const [scrollPosition, setScrollPosition] = useState({ isAtStart: true, isAtEnd: false })

    // Handle scroll to update gradient visibility
    const handleScroll = () => {
        if (!scrollContainerRef.current) return
        const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current
        const isAtStart = scrollLeft <= 1
        const isAtEnd = scrollLeft >= scrollWidth - clientWidth - 1
        setScrollPosition({ isAtStart, isAtEnd })
    }

    // Add scroll listener
    React.useEffect(() => {
        const container = scrollContainerRef.current
        if (!container) return
        // Initial check
        handleScroll()
        container.addEventListener('scroll', handleScroll)
        return () => container.removeEventListener('scroll', handleScroll)
    }, [files.length])

    // Mouse drag scroll functionality
    const onMouseDown = (e: React.MouseEvent) => {
        setIsDragging(true)
        setStartX(e.pageX - (scrollContainerRef.current?.offsetLeft || 0))
        setScrollLeft(scrollContainerRef.current?.scrollLeft || 0)
    }

    const onMouseUp = () => {
        setIsDragging(false)
    }

    const onMouseMove = (e: React.MouseEvent) => {
        if (!isDragging) return
        e.preventDefault()
        
        const x = e.pageX - (scrollContainerRef.current?.offsetLeft || 0)
        const walk = (x - startX) * 1.5 // scroll-fast factor
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollLeft = scrollLeft - walk
        }
    }

    // Handle clear all files
    const handleClearAll = () => {
        if (onFileDelete) {
            files.forEach(file => onFileDelete(file))
        }
    }

    // Empty state
    if (files.length === 0) {
        return null; // Don't render anything if no files
    }
    

    return (
        <div className="relative">
            {/* Hover area container with safety triangle */}
            <div
                className="relative"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                {/* Safety triangle - invisible area that prevents accidental mouse leave */}
                <div 
                    className="absolute -top-12 right-0 w-32 h-12 pointer-events-auto"
                    style={{
                        clipPath: 'polygon(100% 100%, 100% 0%, 75% 100%)'
                    }}
                />

                {/* Erase all button - positioned absolutely outside and above the main container */}
                <AnimatePresence>
                    {isHovered && allowDelete && files.length > 1 && (
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                            transition={{ duration: 0.2 }}
                            className="absolute -top-12 right-0 z-20 h-12 w-40 flex items-end justify-end "
                            onMouseEnter={() => setIsHovered(true)}
                        >
                            <p
                                onClick={handleClearAll}
                                className="text-xs font-medium text-muted-foreground hover:cursor-pointer hover:text-foreground underline"
                            >
                                Erase All
                            </p>
                        </motion.div>
                    )}
                </AnimatePresence>

                <div className={cn("w-full relative", className)}>
                    {/* Left gradient */}
                    <div 
                        className={cn(
                            "absolute left-0 top-0 bottom-0 w-20 z-10 pointer-events-none transition-opacity duration-300",
                            scrollPosition.isAtStart ? "opacity-0" : "opacity-100"
                        )}
                        style={{
                            background: `linear-gradient(to right, hsl(var(--${bgGradientColor || 'background'})), transparent)`
                        }}
                    />
                    
                    {/* Right gradient */}
                    <div 
                        className={cn(
                            "absolute right-0 top-0 bottom-0 w-20 z-10 pointer-events-none transition-opacity duration-300",
                            scrollPosition.isAtEnd ? "opacity-0" : "opacity-100"
                        )}
                        style={{
                            background: `linear-gradient(to left, hsl(var(--${bgGradientColor || 'background'})), transparent)`
                        }}
                    />

                    {/* File cards container */}
                    <motion.div 
                        ref={scrollContainerRef}
                        className="flex overflow-x-auto overflow-y-hidden gap-2 w-full no-scrollbar"
                        onMouseDown={onMouseDown}
                        onMouseUp={onMouseUp}
                        onMouseLeave={onMouseUp}
                        onMouseMove={onMouseMove}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.3 }}
                    >
                        <AnimatePresence initial={false}>
                            {files.map((file) => (
                                <motion.div 
                                    key={file.id}
                                    layout
                                    initial={{ opacity: 0, scale: 0.8 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ 
                                        opacity: 0, 
                                        scale: 0.8,
                                        transition: { duration: 0.05, ease: "easeOut" }
                                    }}
                                    transition={{ 
                                        type: "spring", 
                                        stiffness: 300, 
                                        damping: 25,
                                        opacity: { duration: 0.2 }
                                    }}
                                    className="flex-shrink-0"
                                >
                                    <FileCard
                                        file={file}
                                        onDelete={onFileDelete ? (f) => onFileDelete(f as ContentFile) : undefined}
                                        allowDelete={allowDelete}
                                    />
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </motion.div>
                </div>
            </div>
        </div>
    )
} 